/**
 * Lokal regel-baserad korrekturläsare för svenska anteckningstexter.
 * Inga API-anrop — körs helt i frontend.
 *
 * Fixar:
 * - Trimma whitespace i början/slut
 * - Dubbla mellanslag → enkelt mellanslag (per rad)
 * - Mellanslag före skiljetecken (.,!?;:) tas bort
 * - Mellanslag efter skiljetecken läggs till om det saknas
 * - Stor bokstav i början av texten (eller varje ord om titleCase=true)
 * - Stor bokstav efter punkt/utrop/fråge-tecken
 * - Slutpunkt om texten saknar avslutande skiljetecken (≥2 ord)
 *
 * @param {string} input
 * @param {Object} [options]
 * @param {boolean} [options.addTrailingPeriod=true]  Lägg till punkt sist
 * @param {boolean} [options.titleCase=false]         Stor bokstav i varje ord
 *
 * Returnerar: { fixed, changes } där changes är en lista av beskrivningar.
 * Om inga ändringar gjordes är changes [] och fixed === input.
 */
export const proofreadText = (input, options = {}) => {
  const { addTrailingPeriod = true, titleCase = false } = options;
  if (input === null || input === undefined || typeof input !== 'string') {
    return { fixed: input ?? '', changes: [] };
  }

  let result = input;
  const changes = [];

  // 1. Trim ytterst
  const trimmed = result.replace(/^[ \t]+|[ \t]+$/gm, '').replace(/^\s+|\s+$/g, '');
  if (trimmed !== result) {
    result = trimmed;
    changes.push('Tog bort mellanslag i början/slut');
  }

  // 2. Kollapsa dubbla mellanslag (behåll radbrytningar)
  if (/[ \t]{2,}/.test(result)) {
    result = result.replace(/[ \t]{2,}/g, ' ');
    changes.push('Tog bort dubbla mellanslag');
  }

  // 3. Mellanslag före skiljetecken
  if (/[ \t]+([.,!?;:])/.test(result)) {
    result = result.replace(/[ \t]+([.,!?;:])/g, '$1');
    changes.push('Tog bort mellanslag före skiljetecken');
  }

  // 4. Mellanslag efter skiljetecken om saknas (men inte för siffror som 12.5)
  if (/([.,!?;:])([A-ZÅÄÖa-zåäö])/.test(result)) {
    result = result.replace(/([.,!?;:])([A-ZÅÄÖa-zåäö])/g, '$1 $2');
    changes.push('Lade till mellanslag efter skiljetecken');
  }

  // 5. Stor bokstav i början (eller varje ord om titleCase=true)
  if (titleCase) {
    let didTitleCase = false;
    result = result.replace(/(^|\s|-)([a-zåäö])/g, (m, sep, ch) => {
      didTitleCase = true;
      return sep + ch.toUpperCase();
    });
    if (didTitleCase) changes.push('Stor bokstav i varje ord');
  } else {
    const firstLetterRegex = /^(\s*)([a-zåäö])/;
    if (firstLetterRegex.test(result)) {
      result = result.replace(firstLetterRegex, (_, ws, ch) => ws + ch.toUpperCase());
      changes.push('Stor bokstav i början');
    }
  }

  // 6. Stor bokstav efter . ! ? och radbrytning
  let didCapitalizeAfterPunct = false;
  result = result.replace(/([.!?])(\s+)([a-zåäö])/g, (_, p, ws, ch) => {
    didCapitalizeAfterPunct = true;
    return `${p}${ws}${ch.toUpperCase()}`;
  });
  if (didCapitalizeAfterPunct) changes.push('Stor bokstav efter punkt/utrop/frågetecken');

  let didCapitalizeAfterNewline = false;
  result = result.replace(/(\n+\s*)([a-zåäö])/g, (_, ws, ch) => {
    didCapitalizeAfterNewline = true;
    return ws + ch.toUpperCase();
  });
  if (didCapitalizeAfterNewline) changes.push('Stor bokstav i början av rad');

  // 7. Slutpunkt om text har ≥2 ord och saknar avslutande skiljetecken
  if (addTrailingPeriod && result && !/[.!?:)]\s*$/.test(result)) {
    const wordCount = result.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount >= 2) {
      result = result + '.';
      changes.push('Lade till slutpunkt');
    }
  }

  return { fixed: result, changes };
};
